import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Bookmark } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BookmarkButton({ 
  opportunityId, 
  size = 'default',
  variant = 'ghost',
  className = '',
  showText = false,
  onToggle
}) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && opportunityId) {
      axios.get(`${API}/saved`, { withCredentials: true })
        .then(res => {
          setIsSaved(res.data.some(opp => opp.opportunity_id === opportunityId));
        })
        .catch(() => {});
    }
  }, [opportunityId, isAuthenticated]);

  const handleToggle = async (e) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error('Please sign in to save opportunities');
      navigate('/login');
      return;
    }

    setIsLoading(true);
    try {
      if (isSaved) {
        await axios.delete(`${API}/saved/${opportunityId}`, { withCredentials: true });
        setIsSaved(false);
        toast.success('Removed from saved');
        onToggle?.(false);
      } else {
        await axios.post(`${API}/saved/${opportunityId}`, {}, { withCredentials: true });
        setIsSaved(true);
        toast.success('Saved successfully!');
        onToggle?.(true);
      }
    } catch {
      toast.error('Failed to update bookmark');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleToggle}
      variant={variant}
      size={size}
      disabled={isLoading}
      className={`transition-all duration-200 ${
        isSaved 
          ? 'text-emerald-600 hover:text-red-600 hover:bg-red-50' 
          : 'text-slate-400 hover:text-teal-600 hover:bg-teal-50'
      } ${className}`}
      data-testid={`bookmark-button-${opportunityId}`}
    >
      <Bookmark className={`h-${size === 'sm' ? '4' : '5'} w-${size === 'sm' ? '4' : '5'} ${isSaved ? 'fill-current' : ''}`} />
      {showText && (
        <span className="ml-2">{isSaved ? 'Saved' : 'Save'}</span>
      )}
    </Button>
  );
}

export default BookmarkButton;
